import {
	useState,
	useEffect,
	useCallback,
	PropsWithChildren,
	CSSProperties,
	useRef
} from 'react';
import { atom, useRecoilValue } from 'recoil';
import { IContextMenu } from '@types';
import { createPortal } from 'react-dom';
import styles from './ContextMenu.module.scss';
import { Item } from './ContextMenu.Item';
import { ContextMenuContext } from './ContextMenuProvider';
import { useContextMenu } from '@hooks';

type TProps = {
	id: string;
}

export const ContextMenus = atom<IContextMenu[]>({
	key: '',
	default: []
});

export const ContextMenuMain = (props: PropsWithChildren<TProps>) => {
	const { id, children } = props;
	const { closeContext } = useContextMenu();
	const [data, setData] = useState<{ [key: string]: any} | null>(null);
	const [pos, setPos] = useState<{ x: number; y: number}>({ x: 0, y: 0 });
	const contexts = useRecoilValue(ContextMenus);
	const menuRef = useRef<HTMLDivElement>(null);
	const [opacity, setOpacity] = useState(0);

	const getStyles = useCallback((returnRaw = false) => {
		const styles: CSSProperties = {};
		let X = pos.x;
		let Y = pos.y;
		if (menuRef && menuRef.current) {
			const { width, height } = menuRef.current.getBoundingClientRect();
			const winWidth = window.innerWidth;
			const winHeight = window.innerHeight;
			if (X + width > winWidth) {
				X = winWidth - width - 10;
			}
			if (Y + height > winHeight) {
				Y = winHeight - height - 10;
			}
			styles.transform = `translate(${X}px, ${Y}px)`;
		}
		if (returnRaw) {
			return {
				x: X,
				y: Y
			}
		}
		return styles;
	}, [pos]);

	const outsideClick = (e: any) => {
		if (menuRef && menuRef.current && !menuRef.current.contains(e.target)) {
			closeContext(id);
			setOpacity(0);
		}
	}

	useEffect(() => {
		const openContext = contexts.find((context) => context.id === id);
		if (openContext) {
			setData(openContext.data ?? {});
			setPos(openContext.pos);
			document.addEventListener('click', outsideClick);
			setTimeout(() => {
				setOpacity(1);
			}, 0);
		} else {
			setOpacity(0);
			setData(null);
			setPos({ x: 0, y: 0 });
			document.removeEventListener('click', outsideClick);
		}
		return () => {
			document.removeEventListener('click', outsideClick);
		}
	}, [contexts]);

	return (
		<ContextMenuContext.Provider value={{
			id, data, setData, getStyles
		}}>
			{data && createPortal(<div className={styles.menu} style={{ ...getStyles(), opacity }} ref={menuRef}>
				{children}
			</div>, document.body)}
		</ContextMenuContext.Provider>
	);
}

ContextMenuMain.Item = Item;
export const ContextMenu = ContextMenuMain;
